import React from 'react';
import { TravelAbilityWordmark } from './TravelAbilityLogo';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { assessmentData } from '../data/assessmentData';
import { ArrowRight, Users, Percent, Heart, Clock } from 'lucide-react';

const statIcons = {
  0: Users,
  1: Percent,
  2: Heart
};

export const Introduction = ({ onStart }) => {
  const { title, subtitle, introduction } = assessmentData;

  return (
    <div className="min-h-screen bg-[#111111]">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-15"
          style={{ 
            backgroundImage: 'url(https://images.unsplash.com/photo-1633098272154-b97f6e147b00?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHxhY2Nlc3NpYmxlJTIwdHJhdmVsJTIwd2hlZWxjaGFpciUyMGF1dGhlbnRpY3xlbnwwfHx8fDE3NzU5MjE2NzB8MA&ixlib=rb-4.1.0&q=85)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#111111]/60 via-[#111111]/80 to-[#111111]" />
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-8">
          <div className="flex justify-center mb-10">
            <TravelAbilityWordmark />
          </div>
          
          <h1 
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-center text-[#F3F4F6] mb-4 tracking-tight leading-tight"
            data-testid="assessment-title"
          >
            ACCESSIBILITY<br />BUILD-A-PLAN<br />TOOL
          </h1>
          <p className="text-xl sm:text-2xl text-center text-[#FBBF24] font-semibold mb-8">
            {subtitle}
          </p>
        </div>
      </div>

      {/* Stats Section */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
          {introduction.stats.map((stat, index) => {
            const Icon = statIcons[index] || Users;
            const colors = ['#3B82F6', '#10B981', '#FBBF24'];
            return (
              <Card key={index} className="bg-[#1A1A1A] border-[#333333]">
                <CardContent className="p-6 flex items-center gap-4">
                  <div 
                    className="p-3 rounded-xl"
                    style={{ backgroundColor: `${colors[index]}20` }}
                  >
                    <Icon className="w-6 h-6" style={{ color: colors[index] }} />
                  </div>
                  <div>
                    <p className="text-2xl sm:text-3xl font-bold text-[#F3F4F6]">{stat.value}</p>
                    <p className="text-sm text-[#9CA3AF]">{stat.label}</p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Content Section */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <Card className="bg-[#1A1A1A] border-2 border-[#333333]">
          <CardContent className="p-6 sm:p-10">
            <div className="space-y-6">
              {introduction.content.map((paragraph, index) => (
                <p 
                  key={index} 
                  className={`text-lg leading-relaxed ${
                    index === 1
                      ? 'text-[#FBBF24] font-semibold text-xl' 
                      : 'text-[#9CA3AF]'
                  }`}
                >
                  {paragraph}
                </p>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* What Happens Next */}
        <div className="mt-10">
          <h3 className="text-xl font-bold text-[#F3F4F6] mb-4 text-center">What Happens Next</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { step: 1, text: "You complete the assessment", time: "~2 minutes" },
              { step: 2, text: "We send results + personalized recommendation", time: "Instantly" },
              { step: 3, text: "We reach out to schedule a call", time: "If interested" },
              { step: 4, text: "You get the right package for your situation", time: "No pressure" }
            ].map((item) => (
              <div 
                key={item.step}
                className="flex items-start gap-4 p-4 bg-[#1A1A1A] border border-[#333333] rounded-xl"
              >
                <span className="w-8 h-8 flex items-center justify-center rounded-full bg-[#FBBF24]/20 text-[#FBBF24] font-bold text-sm">
                  {item.step}
                </span>
                <div>
                  <p className="text-[#F3F4F6]">{item.text}</p>
                  <p className="text-sm text-[#6B7280]">{item.time}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Time Estimate */}
        <div className="flex items-center justify-center gap-3 mt-8 p-4 bg-[#222222] rounded-xl">
          <Clock className="w-5 h-5 text-[#10B981]" />
          <span className="text-[#9CA3AF]">
            This assessment takes approximately <span className="text-[#F3F4F6] font-semibold">2 minutes</span>
          </span>
        </div>

        {/* CTA Button */}
        <div className="mt-8 text-center">
          <Button
            onClick={onStart}
            className="bg-[#FBBF24] hover:bg-[#F59E0B] text-[#111111] font-bold text-lg px-10 py-6 rounded-xl"
            data-testid="start-assessment-btn"
          >
            Begin Assessment
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-6 px-4 text-center border-t border-[#333333]">
        <p className="text-[#6B7280] text-sm">No pressure. Just clarity.</p>
      </footer>
    </div>
  );
};

export default Introduction;
